import * as React from 'react';
import { PointerEventWrapper, PointerEventProps } from './PointerEvents';
import { convertToStatefulComponentClass, mergeEventHandlers, getComponentName } from './ReactHelpers';
import { GlobalPointerUpListener } from './GlobalPointerUpListener';

export interface Interactive {
  /**
   * TFS562843
   * To prevent asynchronous calls from calling setState on an unmounted interactive component we set an isMounted flag
   * to true at initializion and false on componentWillUnmount, then check this flag before setState calls.
   */
  interactiveIsMounted: boolean;
  removePointerUpListener?: () => void;
}

export interface InteractiveStyles {
  hover?: React.CSSProperties;
  focus?: React.CSSProperties;
  active?: React.CSSProperties;
  disabled?: React.CSSProperties;
}

export interface InteractiveDomProps extends PointerEventProps {
  interactiveStyles?: InteractiveStyles;
  handleHover?: (hover: boolean) => void;
  handleActive?: (active: boolean) => void;
  handleFocus?: (focus: boolean) => void;
}

export interface InteractiveDomState {
  hover?: boolean;
  active?: boolean;
  focus?: boolean;
}

type InteractiveDomType = React.Component<InteractiveDomProps, InteractiveDomState> & Interactive;

function reactElementIsDomElement(type: React.ReactElement['type']): boolean {
  // per JSX API, when `type` is a string that starts with a lower-case letter, it's a DOM element
  // maybe not all children are JSX elements, but checking that the type is a string rather than a symbol is a safe-enough assumption
  // https://reactjs.org/docs/jsx-in-depth.html#user-defined-components-must-be-capitalized
  return typeof type === 'string' && type.charAt(0) === type.charAt(0).toLowerCase();
}

/**
 * This returns a combined styling with style as it's base. Depending upon the state of the component it assigns
 * onto it the hover, focus, and active styling while overwriting in that order. If disabled, the
 * hover/active/focus styles do not get assigned, but the disabled styling gets assigned.
 */
function resolveInteractiveStyles<P extends InteractiveDomProps>(
  style: React.CSSProperties,
  component: React.Component<P, InteractiveDomState>
): React.CSSProperties {
  const { disabled, interactiveStyles } = component.props;

  if (!interactiveStyles) {
    return style;
  }

  return {
    ...style,
    ...(component.state.hover && !disabled && interactiveStyles.hover),
    ...(component.state.focus && !disabled && interactiveStyles.focus),
    ...(component.state.active && !disabled && interactiveStyles.active),
    ...(disabled && interactiveStyles.disabled)
  };
}

/**
 * We must add the pointerUp styling events to the window instead of the element so that the
 * user can mouse down on the element, move off it, mouseUp, and the element can be
 * made to have inactive styling.
 */
const attachPointerUpListener = (component: InteractiveDomType): void => {
  const handlePointerUp = () => {
    if (!component.interactiveIsMounted) { return; }
    component.setState({ active: false });
    if (component.props.handleActive) {
      component.props.handleActive(false);
    }
    component.removePointerUpListener?.();
    component.removePointerUpListener = undefined;
  };

  component.removePointerUpListener = GlobalPointerUpListener.subscribe(handlePointerUp);
};

function renderInteractiveComponent(component: InteractiveDomType, domElement: React.ReactElement): JSX.Element {
  const { disabled, pointerDown } = component.props;
  const pointerEventProps: PointerEventProps = disabled ? {} : {
    pointerDown: mergeEventHandlers(pointerDown!, (): void => {
      if (!component.interactiveIsMounted) { return; }
      component.setState({ active: true });
      if (component.props.handleActive) {
        component.props.handleActive(true);
      }
      if (!component.removePointerUpListener) {
        attachPointerUpListener(component);
      }
    })
  };

  // hack to reach down another layer in the situation that multiple HOCs are stacked
  const childProps = reactElementIsDomElement(domElement.type) || !domElement.props.children ?
    domElement.props :
    React.Children.only(domElement.props.children).props;

  const {
    onMouseOver,
    onMouseOut,
    onKeyDown,
    onKeyUp,
    onFocus,
    onBlur
  } = childProps;

  const domEventProps: React.HTMLAttributes<HTMLElement> = disabled ? {} : {
    onMouseOver: mergeEventHandlers(onMouseOver, (): void => {
      if (!component.interactiveIsMounted) { return; }
      component.setState({ hover: true });
      if (component.props.handleHover) {
        component.props.handleHover(true);
      }
    }),
    onMouseOut: mergeEventHandlers(onMouseOut, (): void => {
      if (!component.interactiveIsMounted) { return; }
      component.setState({ hover: false });
      if (component.props.handleHover) {
        component.props.handleHover(false);
      }
    }),
    onKeyDown: mergeEventHandlers(onKeyDown, (e: React.KeyboardEvent<HTMLElement>): void => {
      if (!component.interactiveIsMounted) { return; }
      if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
        component.setState({ active: true });
        if (component.props.handleActive) {
          component.props.handleActive(true);
        }
      }
    }),
    onKeyUp: mergeEventHandlers(onKeyUp, (e: React.KeyboardEvent<HTMLElement>): void => {
      if (!component.interactiveIsMounted) { return; }
      if (e.key === ' ' || e.key === 'Enter') {
        component.setState({ active: false });
        if (component.props.handleActive) {
          component.props.handleActive(false);
        }
      }
    }),
    onFocus: mergeEventHandlers(onFocus, (): void => {
      if (!component.interactiveIsMounted) { return; }
      component.setState({ focus: true });
      if (component.props.handleFocus) {
        component.props.handleFocus(true);
      }
    }),
    onBlur: mergeEventHandlers(onBlur, (): void => {
      if (!component.interactiveIsMounted) { return; }
      component.setState({ focus: false });
      if (component.props.handleFocus) {
        component.props.handleFocus(false);
      }
    }),
  };

  const resolvedStyles = resolveInteractiveStyles(domElement.props.style, component);
  const resolvedProps = { style: resolvedStyles, disabled, ...domEventProps };
  const resolvedPointerEventProps = { ...component.props, ...pointerEventProps };

  return (
    <PointerEventWrapper {...resolvedPointerEventProps}>
      {React.cloneElement(domElement, resolvedProps)}
    </PointerEventWrapper>
  );
}

/**
 * Wrap a DOM component to give it interactive styles and pointer events. All events from the PointerEventWrapper can also be used here.
 * Example that attaches a click to a div and gives different styling on hover:
 *
 *    const interactiveProps = {
 *      pointerClick: () => console.log('click'),
 *      interactiveStyles: {
 *        hover: { color: 'salmon' }
 *      }
 *    };
 *
 *    // DOM attributes should go straight to the DOM element
 *    const domProps = {
 *      style: { color: 'fallow' },
 *      onBlur: () => console.log('blur')
 *    };
 *
 *    <InteractiveDomWrapper {...props}>
 *      <div {...domProps} />
 *    </InteractiveDomWrapper>
 *
 * When using, you can only have one child and it must render its props to a DOM component.
 * If there is not exactly one child an exception will be thrown.
 */
export class InteractiveDomWrapper extends React.Component<InteractiveDomProps, InteractiveDomState> implements Interactive {
  public removePointerUpListener: () => void;
  public interactiveIsMounted: boolean;

  public constructor(props: InteractiveDomProps) {
    super(props);
    this.state = {
      ...this.state,
      hover: false,
      active: false,
      focus: false,
    };
    this.interactiveIsMounted = true;
  }

  public static getDerivedStateFromProps(props: InteractiveDomProps, state: InteractiveDomState): Partial<InteractiveDomState> | null {
    if (props.disabled && (state.active || state.focus || state.hover)) {
      return {
        active: false,
        focus: false,
        hover: false,
      };
    }

    return null;
  }

  public componentWillUnmount(): void {
    this.interactiveIsMounted = false;
    if (this.removePointerUpListener) {
      this.removePointerUpListener();
    }
  }

  public render(): JSX.Element {
    // reuse the child. It could be anything - div, ul, button, etc.
    return renderInteractiveComponent(this, React.Children.only(this.props.children!));
  }
}

/**
 * Higher order component providing CSS :pseudo classes behavior and pointer events. All events from the PointerEventWrapper
 * can also be used here.
 *
 * You can use this to wrap either stateless components or component classes. To use as a @Decorator,
 * use the InteractiveDomHoCDecorator
 */
export const InteractiveDomHoC = <P extends InteractiveDomProps>(componentType: React.ComponentType<P>): React.ComponentClass<P> => {
  const componentClass: React.ComponentClass<P> = convertToStatefulComponentClass(componentType);

  const newComponentClass: React.ComponentClass<P> = class extends componentClass implements Interactive {
    public static displayName: string = `InteractiveDom(${getComponentName(componentType)})`;
    public removePointerUpListener: () => void;
    public interactiveIsMounted: boolean;

    public constructor(props: P) {
      super(props);
      this.state = {
        ...this.state,
        hover: false,
        active: false,
        focus: false,
      };
      this.interactiveIsMounted = true;
    }

    public static getDerivedStateFromProps(props: P, state: InteractiveDomState): Partial<InteractiveDomState> | null {
      if (props.disabled && (state.active || state.focus || state.hover)) {
        return {
          active: false,
          focus: false,
          hover: false,
        };
      }

      return null;
    }

    /**
     * Why no super?  React.Component does not actually implement the component lifecycle methods, but checks the instance
     * at runtime to see if they are defined.  Therefore super.componentWillUnmount property does not exist at compile time.
     * Instead we look to see if componentWillUnmount exists on the componentClass by looking at it's prototype and calling it
     * if so.
     */
    private callComponentClassWillUnmount(): void {
      // tslint:disable-next-line:no-string-literal
      const componentClassWillUnmount = componentClass.prototype['componentWillUnmount'];

      if (componentClassWillUnmount) {
        componentClassWillUnmount.apply(this);
      }
    }

    public componentWillUnmount(): void {
      this.callComponentClassWillUnmount();

      this.interactiveIsMounted = false;
      if (this.removePointerUpListener) {
        this.removePointerUpListener();
      }
    }

    public render(): JSX.Element {
      return renderInteractiveComponent(this, super.render() as JSX.Element);
    }
  };
  return newComponentClass;
};

/** Do some type magic so this can be used as a @Decorator on a component class */
// tslint:disable:no-any
export const InteractiveDomHoCDecorator: ClassDecorator = InteractiveDomHoC as any;
